import { PageHeader, Skeleton, SkeletonRegion } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { STACK, PANEL_PAD } from '../../constants/density';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import { MobileScreen, MobileHeader } from '../../mobile';

/**
 * «حسابداری» while it is waiting — the hub's own shape in placeholder blocks
 * (DESIGN.md §"Loading & skeleton").
 *
 * It is VIEW-AWARE because the hub's eight destinations do not have one shape: a
 * ledger is a KPI strip over a wide table, «حساب‌های مالی» is a short table with no
 * strip, and the dashboard is tiles. Drawing the ledger's eight columns while the
 * dashboard's chunk is in flight is a flash of the wrong page, which is exactly
 * what this registry exists to prevent.
 *
 * LIGHT by contract: it ships in the MAIN bundle as the lazy chunk's fallback, so
 * it imports nothing from the screen, its views or its hooks — kit primitives,
 * tokens, density and i18n only.
 */

/** How many columns each view's table has, and whether it carries a KPI strip. */
const SHAPE = {
  dashboard: { cols: 0, kpis: 4 },
  transactions: { cols: 8, kpis: 3 },
  receipts: { cols: 8, kpis: 4 },
  payments: { cols: 7, kpis: 4 },
  accounts: { cols: 6, kpis: 0 },
  counterparties: { cols: 5, kpis: 0 },
  receivables: { cols: 6, kpis: 3 },
  payables: { cols: 5, kpis: 3 },
};

const shapeOf = (view) => SHAPE[view] || SHAPE.receipts;

function KpiStripSkeleton({ count }) {
  if (!count) return null;
  return (
    <div aria-hidden style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} width={180} height={84} radius={16} />
      ))}
    </div>
  );
}

function TableSkeleton({ cols, rows = 8 }) {
  if (!cols) return null;
  return (
    <div
      aria-hidden
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', gap: 12, padding: '13px 20px', background: '#f8fafc', borderBottom: `2px solid ${C.border}` }}>
        {Array.from({ length: cols }, (_, i) => <Skeleton key={i} width={70} height={11} />)}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} style={{ display: 'flex', gap: 12, padding: '13px 20px', borderTop: `1px solid ${C.border}` }}>
          {Array.from({ length: cols }, (_, i) => <Skeleton key={i} width={i === 1 ? 140 : 72} height={13} />)}
        </div>
      ))}
    </div>
  );
}

/** The desktop workbench: fixed header, the rail beside one scrolling surface. */
export function AccountingSkeletonWeb({ view, rows = 8 }) {
  const { t } = useTranslation();
  const shape = shapeOf(view);

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bg, overflow: 'hidden' }}
      data-testid="accounting-skeleton"
      data-view={view || ''}
    >
      {/* The chrome renders for real and is never skeletoned: the panel must not
          forget what page it is on while its data loads. */}
      <PageHeader title={t('accounting.hub.title')} sub={t('accounting.hub.subtitle')} />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div
          aria-hidden
          style={{
            width: 176, flexShrink: 0, borderInlineEnd: `1px solid ${C.border}`,
            background: C.card, padding: '12px 8px',
            display: 'flex', flexDirection: 'column', gap: STACK.tight,
          }}
        >
          {Array.from({ length: 8 }, (_, i) => <Skeleton key={i} width="100%" height={30} radius={8} />)}
        </div>

        <SkeletonRegion
          label={t('accounting.hub.title')}
          style={{ flex: 1, minWidth: 0, overflow: 'hidden', padding: PANEL_PAD.regular }}
        >
          <KpiStripSkeleton count={shape.kpis} />
          <TableSkeleton cols={shape.cols} rows={rows} />
        </SkeletonRegion>
      </div>
    </div>
  );
}

/** The phone: the header and the section strip, over card-shaped placeholders. */
export function AccountingSkeletonMobile({ view, cards = 5 }) {
  const { t } = useTranslation();

  return (
    <MobileScreen
      data-testid="accounting-skeleton"
      data-view={view || ''}
      header={<MobileHeader sticky title={t('accounting.hub.title')} subtitle={t('accounting.hub.subtitle')} />}
    >
      <SkeletonRegion label={t('accounting.hub.title')} style={{ display: 'grid', gap: STACK.regular, padding: '0.75rem 1rem' }}>
        {Array.from({ length: cards }, (_, i) => (
          <Skeleton key={i} width="100%" height={72} radius={12} />
        ))}
      </SkeletonRegion>
    </MobileScreen>
  );
}

/** The route's fallback: whichever surface the viewport is on. */
export default function AccountingSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <AccountingSkeletonMobile {...props} /> : <AccountingSkeletonWeb {...props} />;
}
